/**
 * Navigation Tabs Component
 *
 * Main navigation for switching between Canvas and AI Workflows modes
 */

import React, { useState, useRef, useEffect } from 'react';
import { Palette, Sparkles, BookOpen, Clock, ChevronDown, Plus, GitBranch, List, FolderOpen, Shield, Wrench, Network, Notebook, ExternalLink } from 'lucide-react';

const MODES = [
  {
    id: 'canvas',
    label: 'Canvas',
    icon: Palette,
    description: 'Visual graph workspace'
  },
  {
    id: 'workflows',
    label: 'AI Workflows',
    icon: Sparkles,
    description: 'Task decomposition & automation',
    hasDropdown: true,
    subModes: [
      {
        id: 'workflows-create',
        label: 'Create New',
        icon: Plus,
        description: 'Start a new workflow'
      },
      {
        id: 'workflows-visualization',
        label: 'Workflow Graph',
        icon: GitBranch,
        description: 'View workflow visualization'
      }
    ]
  },
  {
    id: 'files',
    label: 'Data',
    icon: FolderOpen,
    description: 'Manage uploaded files'
  },
  {
    id: 'workflows-history',
    label: 'History',
    icon: Clock,
    description: 'View past workflows'
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: BookOpen,
    description: 'Pre-built workflow templates'
  },
  {
    id: 'tools',
    label: 'Tools',
    icon: Wrench,
    description: 'Browse and manage available tools'
  },
  {
    id: 'notebook',
    label: 'Notebook',
    icon: Notebook,
    description: 'Query your data & call AI from a notebook',
    external: true,
    href: 'https://marimo.flowize.xyz'
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    description: 'Admin dashboard (costs & usage)',
    adminOnly: true
  },
  {
    id: 'fsm-registry',
    label: 'FSM Registry',
    icon: Network,
    description: 'Domain FSM definitions (dev/admin)',
    adminOnly: true
  }
];

export default function NavigationTabs({ currentMode, onModeChange, isAdmin = false }) {
  const [showWorkflowDropdown, setShowWorkflowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Filter modes based on admin status
  const visibleModes = MODES.filter(mode => !mode.adminOnly || isAdmin);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowWorkflowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleWorkflowClick = () => {
    const workflowSubModeIds = MODES.find(m => m.id === 'workflows')?.subModes.map(s => s.id) ?? [];
    if (workflowSubModeIds.includes(currentMode)) {
      setShowWorkflowDropdown(!showWorkflowDropdown);
    } else {
      onModeChange('workflows-create');
    }
  };

  const handleSubModeClick = (subModeId) => {
    onModeChange(subModeId);
    setShowWorkflowDropdown(false);
  };

  // Two-state contract for every nav tab:
  //   rest    → gray text, no bg
  //   hover   → bg-gray-100 + darker text + bottom indicator at 40% opacity
  //   active  → indigo text + full-strength 2px underline
  //   pressed → translate-y for tactile feedback (~80ms)
  //   focus   → visible indigo ring (keyboard a11y)
  const tabBase =
    'group relative flex items-center gap-2 px-3 py-2 rounded-md font-medium text-sm ' +
    'transition-colors duration-150 cursor-pointer ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ' +
    'active:translate-y-px';

  const tabClass = (isActive) =>
    isActive
      ? `${tabBase} text-indigo-700`
      : `${tabBase} text-gray-600 hover:text-gray-900 hover:bg-gray-100`;

  // 2px bottom indicator: full strength on active, hover-fades-in on inactive.
  // Sits 1px below the rounded button so it lines up with the panel's bottom border.
  const indicator = (isActive) => (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute -bottom-[9px] left-3 right-3 h-[2px] rounded-full bg-indigo-600 transition-opacity duration-150 ${
        isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'
      }`}
    />
  );

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1 py-2">
          {visibleModes.map((mode) => {
            const Icon = mode.icon;
            const isActive = mode.hasDropdown
              ? mode.subModes.some(sub => sub.id === currentMode)
              : currentMode === mode.id;

            // Special handling for workflows with dropdown
            if (mode.hasDropdown) {
              return (
                <div key={mode.id} className="relative" ref={dropdownRef}>
                  <button
                    onClick={handleWorkflowClick}
                    className={tabClass(isActive)}
                    title={mode.description}
                  >
                    <Icon size={18} />
                    <span>{mode.label}</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-150 ${showWorkflowDropdown ? 'rotate-180' : ''}`}
                    />
                    {indicator(isActive)}
                  </button>

                  {/* Dropdown menu */}
                  {showWorkflowDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                      {mode.subModes.map((subMode) => {
                        const SubIcon = subMode.icon;
                        const isSubActive = currentMode === subMode.id;

                        return (
                          <button
                            key={subMode.id}
                            onClick={() => handleSubModeClick(subMode.id)}
                            className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors duration-150 cursor-pointer focus:outline-none focus-visible:bg-gray-100 ${
                              isSubActive
                                ? 'bg-indigo-50 text-indigo-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            title={subMode.description}
                          >
                            <SubIcon size={16} />
                            <span className="text-sm">{subMode.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // External-link tab — opens in a new tab. Never marked active
            // (we never "are on" an external destination from inside /app).
            if (mode.external) {
              return (
                <a
                  key={mode.id}
                  href={mode.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={tabClass(false)}
                  title={mode.description}
                >
                  <Icon size={18} />
                  <span>{mode.label}</span>
                  <ExternalLink size={12} className="opacity-60" />
                  {indicator(false)}
                </a>
              );
            }

            // Regular tabs without dropdown
            return (
              <button
                key={mode.id}
                onClick={() => onModeChange(mode.id)}
                className={tabClass(isActive)}
                title={mode.description}
              >
                <Icon size={18} />
                <span>{mode.label}</span>
                {indicator(isActive)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
